/**
 * Validation Utilities
 * 
 * Provides validation functions for user input, OAuth parameters,
 * and security-related data.
 */

import { PasswordStrength } from '@/types/auth.types';

/**
 * Validate email address format
 * 
 * Uses a comprehensive regex pattern that covers most valid email formats
 * while rejecting obviously invalid ones.
 * 
 * @param email - The email address to validate
 * @returns True if the email format is valid
 */
export function validateEmail(email: string): boolean {
  if (!email || typeof email !== 'string') {
    return false;
  }
  
  // RFC 5322 compliant email regex (simplified)
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  
  return emailRegex.test(email) && email.length <= 254;
}

/**
 * Validate password strength
 * 
 * Checks password against security requirements:
 * - Minimum 8 characters
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one number
 * - At least one special character
 * 
 * @param password - The password to validate
 * @returns PasswordStrength object with validation results
 */
export function validatePasswordStrength(password: string): PasswordStrength {
  const requirements = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
  };
  
  const feedback: string[] = [];
  
  if (!requirements.minLength) {
    feedback.push('Password must be at least 8 characters long');
  }
  if (!requirements.hasUppercase) {
    feedback.push('Password must contain at least one uppercase letter');
  }
  if (!requirements.hasLowercase) {
    feedback.push('Password must contain at least one lowercase letter');
  }
  if (!requirements.hasNumber) {
    feedback.push('Password must contain at least one number');
  }
  if (!requirements.hasSpecialChar) {
    feedback.push('Password must contain at least one special character');
  }
  
  // Calculate score (0-4)
  const metRequirements = Object.values(requirements).filter(Boolean).length;
  const score = Math.min(4, metRequirements);
  
  const isValid = Object.values(requirements).every(Boolean);
  
  return {
    score,
    feedback,
    isValid,
    requirements,
  };
}

/**
 * OAuth authorization request parameters
 */
export interface OAuthParams {
  client_id: string;
  redirect_uri: string;
  response_type?: string;
  scope?: string;
  state?: string;
  code_challenge?: string;
  code_challenge_method?: string;
}

/**
 * Validation result
 */
export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

/**
 * Validate OAuth authorization parameters
 * 
 * Ensures all required parameters are present and properly formatted
 * according to OAuth 2.0 and PKCE specifications.
 * 
 * @param params - The OAuth parameters to validate
 * @returns ValidationResult indicating if parameters are valid
 */
export function validateOAuthParams(params: OAuthParams): ValidationResult {
  const errors: string[] = [];
  
  // Required parameters
  if (!params.client_id || typeof params.client_id !== 'string' || params.client_id.trim() === '') {
    errors.push('client_id is required and must be a non-empty string');
  }
  
  if (!params.redirect_uri || typeof params.redirect_uri !== 'string') {
    errors.push('redirect_uri is required and must be a string');
  } else {
    try {
      new URL(params.redirect_uri);
    } catch {
      errors.push('redirect_uri must be a valid URL');
    }
  }
  
  // Response type validation (should be 'code' for authorization code flow)
  if (params.response_type && params.response_type !== 'code') {
    errors.push('response_type must be "code" for authorization code flow');
  }
  
  // PKCE validation
  if (params.code_challenge) {
    if (typeof params.code_challenge !== 'string' || params.code_challenge.trim() === '' || params.code_challenge.trim().length < 43) {
      errors.push('code_challenge must be at least 43 characters long');
    }
    
    if (params.code_challenge_method && !['S256', 'plain'].includes(params.code_challenge_method)) {
      errors.push('code_challenge_method must be either "S256" or "plain"');
    }
  }
  
  // State parameter validation (recommended for CSRF protection)
  if (params.state && (typeof params.state !== 'string' || params.state.trim() === '' || params.state.trim().length < 8)) {
    errors.push('state parameter should be at least 8 characters long');
  }
  
  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Validate redirect URI against a whitelist
 * 
 * Ensures the redirect URI is in the list of allowed URIs for the client.
 * Performs exact string matching for security, with one exception:
 * 
 * If the client has any registered redirect URI containing a "dev" subdomain
 * (e.g., https://dev-ops.nodegeeks.org), then http://localhost on any port
 * is also accepted. This enables local development without needing to register
 * every developer's local port in the platform record.
 * 
 * @param uri - The redirect URI to validate
 * @param allowedUris - Array of allowed redirect URIs
 * @returns True if the URI is in the whitelist or matches localhost for dev clients
 */
export function validateRedirectUri(uri: string, allowedUris: string[]): boolean {
  if (!uri || !Array.isArray(allowedUris)) {
    return false;
  }
  
  // Exact match first
  if (allowedUris.includes(uri)) {
    return true;
  }

  // Allow localhost (any port, including subdomains like acme-corp.localhost)
  // if the client has a dev-subdomain redirect URI registered
  try {
    const parsed = new URL(uri);
    const isLocalhost = parsed.hostname === 'localhost'
      || parsed.hostname === '127.0.0.1'
      || parsed.hostname.endsWith('.localhost');
    
    if (isLocalhost) {
      const hasDevUri = allowedUris.some((allowed) => {
        try {
          const allowedUrl = new URL(allowed);
          return allowedUrl.hostname.startsWith('dev-') || allowedUrl.hostname.startsWith('dev.');
        } catch {
          return false;
        }
      });

      if (hasDevUri) {
        return true;
      }
    }
  } catch {
    // URI is not a valid URL — fall through to return false
  }

  return false;
}
